import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { PageHeader, EmptyState, fmtDate } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { FileText, Plus, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [scans, setScans] = useState([]);
  const [open, setOpen] = useState(false);
  const [scanId, setScanId] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = () => {
    api.get("/reports").then((r) => setReports(r.data));
    api.get("/scans").then((r) => setScans(r.data.filter((s) => s.status === "completed")));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports", { scan_id: scanId });
      toast.success("Report generated", { description: data.ai_generated ? "AI narratives included" : "Template narratives used" });
      setOpen(false);
      setScanId("");
      navigate(`/reports/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fade-up">
      <PageHeader
        overline="Deliverables"
        title="Reports"
        subtitle="Industry-standard reports (PTES / OWASP / OSCP) with AI-assisted narratives and branded PDF export."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-report-button" className="bg-blue-600 hover:bg-blue-500 text-white">
                <Plus size={17} weight="bold" className="mr-1.5" /> Generate Report
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white">
              <DialogHeader><DialogTitle>Generate Report</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">Completed scan</Label>
                  <Select value={scanId} onValueChange={setScanId}>
                    <SelectTrigger data-testid="report-scan-select" className="mt-1 bg-[#0f172a] border-[#1e293b]">
                      <SelectValue placeholder="Select a completed scan" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b1220] border-[#1e293b] text-white">
                      {scans.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.type} — {s.target_name} ({s.findings_count} findings)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md bg-blue-500/10 border border-blue-500/30 px-3 py-2.5 flex gap-2 text-sm text-blue-200">
                  <Sparkle size={17} weight="fill" className="shrink-0 mt-0.5" />
                  Executive summary &amp; remediation narratives are generated by Azure OpenAI (in-region for production).
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="report-generate-submit" onClick={generate} disabled={!scanId || generating}
                  className="bg-blue-600 hover:bg-blue-500 text-white">
                  {generating ? "Generating…" : "Generate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {reports.length === 0 ? (
        <EmptyState icon={FileText} title="No reports yet" hint="Generate a report from any completed scan." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <Card key={r.id} data-testid={`report-card-${r.id}`}
              onClick={() => navigate(`/reports/${r.id}`)}
              className="bg-[#0f172a] border-[#1e293b] rounded-lg p-5 cursor-pointer hover:-translate-y-0.5 hover:border-blue-500/40 transition-transform transition-colors duration-150 group">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <FileText size={20} className="text-blue-400" weight="bold" />
                </div>
                {r.ai_generated && (
                  <span className="inline-flex items-center gap-1 text-xs text-blue-400 font-mono">
                    <Sparkle size={13} weight="fill" /> AI
                  </span>
                )}
              </div>
              <h3 className="font-head text-lg font-semibold text-white mt-4">{r.engagement_name}</h3>
              <div className="text-sm text-slate-400">{r.client_name} · {r.scan_type} assessment</div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1e293b]">
                <span className="text-xs text-slate-500">{fmtDate(r.generated_at)}</span>
                <span className="inline-flex items-center gap-1 text-sm text-blue-400 group-hover:gap-2 transition-all duration-150">
                  Open <ArrowRight size={15} weight="bold" />
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
